/**
 * Slots where the legal completion is an INDEX NAME — not a table, not a
 * column. Sibling of `indexTargetFilter.ts`, which owns the opposite half of
 * the same grammar (the TABLE an index lives on, at `CREATE INDEX ix ON ▮`).
 *
 * Two shapes:
 *
 *   1. `index-hint` — MYSQL-ONLY SYNTAX. `FROM rental r FORCE INDEX (▮)`,
 *      plus `USE` / `IGNORE`, `KEY` for `INDEX`, and the optional scope
 *      `FOR JOIN` / `FOR ORDER BY` / `FOR GROUP BY`. No other engine has
 *      optimizer index hints in this form (T-SQL spells them
 *      `WITH (INDEX(ix))`, Oracle puts them in a specially-marked block
 *      comment, PG and SQLite have none), so the recognizer refuses every
 *      engine but MySQL rather than matching by shape. The hint binds to the
 *      relation it follows, so the vocabulary is THAT relation's indexes.
 *
 *   2. `drop-index` — the index a DROP statement names. Spelled with the
 *      owning table on MySQL / T-SQL (`DROP INDEX ▮ ON t`, and MySQL's
 *      `ALTER TABLE t DROP INDEX ▮`) and without it on PG / SQLite / Oracle
 *      (`DROP INDEX [CONCURRENTLY] [IF EXISTS] ▮`, where the index name is
 *      schema-scoped, not table-scoped). When the statement names a table the
 *      vocabulary narrows to that table's indexes; when it does not, every
 *      index the snapshot knows is a legal answer.
 *
 * `relation: null` therefore means "not narrowed", never "not found".
 *
 * Data availability is the caller's problem: `TableMeta.indexes` is populated
 * for MySQL, MSSQL and SQLite today and undefined for PG / Oracle (see
 * `IndexMeta`). An engine with no data emits nothing, which is why the
 * emitter below does NOT claim the slot when it has no candidates — falling
 * through to the ordinary emission is better than an empty widget on an
 * engine whose adapter has not caught up.
 */

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';

import { dropAttachedPartialTokens, tokenize, type Token } from '../context/tokenizer';

/** Cheap gate for {@link detectIndexNameSlot} — see the comment at its use. */
const INDEX_OR_KEY_RE = /\b(?:INDEX|KEY)\b/i;

export type IndexNameSlot = {
  readonly kind: 'index-hint' | 'drop-index';
  /** The relation whose indexes are legal here, or null when the shape does
   *  not name one (PG / SQLite / Oracle `DROP INDEX`). */
  readonly relation: { readonly schema?: string; readonly name: string } | null;
};

/** The subset of `IndexMeta` this module reads. */
export type IndexNameEntry = {
  readonly name: string;
  readonly columns: readonly string[];
  readonly isUnique: boolean;
  readonly isPrimary: boolean;
};

/** The table whose indexes the caller should look up. */
export type IndexNameLookupTable = {
  readonly name: string;
  readonly schema?: string;
  readonly indexes?: readonly IndexNameEntry[];
};

/**
 * The index-name slot at the caret, or null. A partially typed name still
 * counts — the slot must hold while the user types or the relation list
 * reappears on the first keystroke.
 */
export function detectIndexNameSlot(
  text: string,
  offset: number,
  statementStart: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): IndexNameSlot | null {
  // Both shapes spell `INDEX` or `KEY` before the caret, so this pre-filter is
  // sound and keeps the tokenize pass off every ordinary DML keystroke.
  if (!INDEX_OR_KEY_RE.test(text.slice(statementStart, offset))) return null;
  const raw = tokenize(text, statementStart, offset, engine);
  const tokens = dropAttachedPartialTokens(raw, offset);
  if (tokens.length === 0) return null;

  if (engine === 'mysql') {
    const hint = mysqlIndexHintSlot(tokens);
    if (hint) return hint;
    const alterDrop = mysqlAlterTableDropIndexSlot(tokens);
    if (alterDrop) return alterDrop;
  }
  return dropIndexSlot(tokens, text, offset, statementEnd, engine);
}

/**
 * Emit the index names legal at the caret, tier 1. Returns true when the slot
 * was claimed AND had something to offer — the caller short-circuits then.
 * See the module header for why an empty vocabulary does not claim the slot.
 */
export function emitIndexNameCandidates(
  out: CompletionCandidate[],
  args: {
    readonly text: string;
    readonly offset: number;
    readonly statementStart: number;
    readonly statementEnd: number;
    readonly engine: DatabaseEngineId;
    readonly tables: readonly IndexNameLookupTable[];
    /** Engine identifier quoting, e.g. backticks for MySQL. Defaults to the
     *  bare name — every engine accepts an unquoted ordinary index name. */
    readonly format?: (name: string) => string;
  },
): boolean {
  const slot = detectIndexNameSlot(
    args.text, args.offset, args.statementStart, args.statementEnd, args.engine,
  );
  if (!slot) return false;
  const format = args.format ?? ((n: string) => n);
  const seen = new Set<string>();
  let emitted = 0;
  for (const table of tablesInScope(args.tables, slot.relation)) {
    for (const index of table.indexes ?? []) {
      const key = index.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        label: index.name,
        insertText: format(index.name),
        isSnippet: false,
        kind: 'system-object',
        tier: 1,
        detail: indexDetail(index),
        filterText: index.name,
      });
      emitted += 1;
    }
  }
  return emitted > 0;
}

function indexDetail(index: IndexNameEntry): string {
  const shape = index.isPrimary ? 'Primary key' : index.isUnique ? 'Unique index' : 'Index';
  return index.columns.length > 0 ? `${shape} (${index.columns.join(', ')})` : shape;
}

function tablesInScope(
  tables: readonly IndexNameLookupTable[],
  relation: IndexNameSlot['relation'],
): readonly IndexNameLookupTable[] {
  if (!relation) return tables;
  return tables.filter(
    (t) =>
      eq(t.name, relation.name) &&
      (relation.schema === undefined || eq(t.schema ?? '', relation.schema)),
  );
}

/**
 * `FROM t [[AS] a] {USE|FORCE|IGNORE} {INDEX|KEY} [FOR {JOIN|ORDER BY|GROUP BY}] (▮`
 * — the caret is inside the hint's parens. A comma-continued second name
 * (`FORCE INDEX (a, ▮)`) is the same slot.
 */
function mysqlIndexHintSlot(tokens: readonly Token[]): IndexNameSlot | null {
  const lparenIdx = enclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return null;
  let i = lparenIdx - 1;
  // The optional `FOR <scope>` suffix, longest first.
  const w = (k: number): string | null => wordOf(tokens[k]);
  if (w(i) === 'JOIN' && w(i - 1) === 'FOR') i -= 2;
  else if ((w(i) === 'BY') && (w(i - 1) === 'ORDER' || w(i - 1) === 'GROUP') && w(i - 2) === 'FOR') {
    i -= 3;
  }
  if (w(i) !== 'INDEX' && w(i) !== 'KEY') return null;
  i -= 1;
  const verb = w(i);
  if (verb !== 'USE' && verb !== 'FORCE' && verb !== 'IGNORE') return null;
  i -= 1;
  return { kind: 'index-hint', relation: relationBefore(tokens, i) };
}

/**
 * The relation a MySQL index hint attaches to: the FROM/JOIN entry directly
 * before the hint verb, past an optional alias (`FROM rental r FORCE …`,
 * `FROM rental AS r FORCE …`). Returns null when no relation name is there —
 * the caller then offers every index rather than guessing.
 *
 * "Is this token the relation or its alias?" is decided by what INTRODUCES the
 * name, not by whether another name precedes it: in `FROM rental FORCE …` the
 * word before `rental` is `FROM`, so `rental` is the relation, while in
 * `FROM rental r FORCE …` nothing introduces `r`, so it is the alias and the
 * walk continues past it.
 */
function relationBefore(tokens: readonly Token[], from: number): IndexNameSlot['relation'] {
  const direct = readQualifiedNameBackward(tokens, from);
  if (!direct) return null;
  if (direct.introduced) return direct.relation;
  // `from` named the alias — step over it (and an explicit `AS`) and read the
  // relation that precedes it.
  let i = direct.startIdx - 1;
  if (wordOf(tokens[i]) === 'AS') i -= 1;
  const outer = readQualifiedNameBackward(tokens, i);
  return outer?.relation ?? null;
}

/** Tokens that introduce a relation name in a FROM clause. */
const RELATION_INTRODUCERS: ReadonlySet<string> = new Set([
  'FROM', 'JOIN', 'STRAIGHT_JOIN', 'UPDATE', 'INTO', 'APPLY', 'TABLE',
]);

/**
 * Read a `[db.][schema.]name` chain BACKWARD from `at`, reporting where the
 * chain starts and whether a relation-introducing token precedes it.
 */
function readQualifiedNameBackward(
  tokens: readonly Token[],
  at: number,
): { relation: NonNullable<IndexNameSlot['relation']>; startIdx: number; introduced: boolean } | null {
  if (!isNameToken(tokens[at])) return null;
  const parts = [unquote(tokens[at]!.text)];
  let startIdx = at;
  while (
    tokens[startIdx - 1]?.type === 'dot'
    && isNameToken(tokens[startIdx - 2])
    && parts.length < 3
  ) {
    parts.push(unquote(tokens[startIdx - 2]!.text));
    startIdx -= 2;
  }
  const before = tokens[startIdx - 1];
  const beforeWord = wordOf(before);
  return {
    relation: {
      name: parts[0]!,
      ...(parts[1] ? { schema: parts[1] } : {}),
    },
    startIdx,
    introduced:
      before?.type === 'comma'
      || (beforeWord !== null && RELATION_INTRODUCERS.has(beforeWord)),
  };
}

/** `ALTER TABLE <tbl> DROP {INDEX|KEY} ▮` — MySQL's in-place index drop. */
function mysqlAlterTableDropIndexSlot(tokens: readonly Token[]): IndexNameSlot | null {
  const last = tokens.length - 1;
  const kw = wordOf(tokens[last]);
  if (kw !== 'INDEX' && kw !== 'KEY') return null;
  if (wordOf(tokens[last - 1]) !== 'DROP') return null;
  const words = tokens.map((t) => wordOf(t));
  if (words[0] !== 'ALTER' || words[1] !== 'TABLE') return null;
  // `ALTER TABLE <name>` — the qualified name starts at token 2.
  const nameEnd = qualifiedNameEndIndex(tokens, 2);
  if (nameEnd < 2) return null;
  return { kind: 'drop-index', relation: qualifiedNameEndingAt(tokens, nameEnd) };
}

/**
 * `DROP INDEX [CONCURRENTLY] [IF EXISTS] ▮ [ON <tbl>]`. The owning table sits
 * AFTER the caret on MySQL / T-SQL, so it is read with a forward scan; PG /
 * SQLite / Oracle have no `ON` clause and leave `relation` null.
 */
function dropIndexSlot(
  tokens: readonly Token[],
  text: string,
  offset: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): IndexNameSlot | null {
  const words = tokens.map((t) => wordOf(t));
  if (words[0] !== 'DROP' || words[1] !== 'INDEX') return null;
  // Everything between `INDEX` and the caret must be the statement's own
  // modifiers — anything else means the name is already written and the caret
  // has moved on (e.g. `DROP INDEX ix ▮` wants `ON`, not another index).
  for (let i = 2; i < words.length; i += 1) {
    if (!DROP_INDEX_MODIFIERS.has(words[i] ?? '')) return null;
  }
  return { kind: 'drop-index', relation: onTargetAfter(text, offset, statementEnd, engine) };
}

const DROP_INDEX_MODIFIERS: ReadonlySet<string> = new Set([
  'CONCURRENTLY', 'IF', 'NOT', 'EXISTS',
]);

/** The relation named by an `ON <tbl>` clause AFTER the caret, or null. */
function onTargetAfter(
  text: string,
  offset: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): IndexNameSlot['relation'] {
  const ahead = tokenize(text, offset, statementEnd, engine);
  for (let i = 0; i < ahead.length; i += 1) {
    if (wordOf(ahead[i]) !== 'ON') continue;
    const end = qualifiedNameEndIndex(ahead, i + 1);
    return end > i ? qualifiedNameEndingAt(ahead, end) : null;
  }
  return null;
}

/** Index of the last token of the qualified name starting at `from`, or
 *  `from - 1` when no name starts there. */
function qualifiedNameEndIndex(tokens: readonly Token[], from: number): number {
  if (!isNameToken(tokens[from])) return from - 1;
  let i = from;
  while (tokens[i + 1]?.type === 'dot' && isNameToken(tokens[i + 2])) i += 2;
  return i;
}

/** The relation named by the `[db.][schema.]name` chain ending at `at`.
 *  `parts[1]` is the immediate qualifier — for a three-part T-SQL name
 *  (`db.dbo.t`) that is the schema, which is what callers match on. */
function qualifiedNameEndingAt(
  tokens: readonly Token[],
  at: number,
): IndexNameSlot['relation'] {
  return readQualifiedNameBackward(tokens, at)?.relation ?? null;
}

/** Index of the innermost `(` still unclosed at the end of the stream, or -1. */
function enclosingLparenIndex(tokens: readonly Token[]): number {
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.type === 'rparen') depth += 1;
    else if (t.type === 'lparen') {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return -1;
}

/** An identifier that can be part of a relation name. `kw` is accepted: a
 *  perfectly ordinary table can be called `order` or `key`. */
function isNameToken(t: Token | undefined): boolean {
  return t !== undefined && (t.type === 'ident' || t.type === 'kw');
}

function wordOf(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return unquote(t.text).toUpperCase();
}

function unquote(s: string): string {
  if (s.length < 2) return s;
  const first = s[0];
  const last = s[s.length - 1];
  if (first === '`' && last === '`') return s.slice(1, -1);
  if (first === '"' && last === '"') return s.slice(1, -1);
  if (first === '[' && last === ']') return s.slice(1, -1);
  return s;
}

function eq(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}
